import React from "react";
import {useTheme} from "@mui/material";

export type ConnectionUnavailableVariant = "notAllowed" | "missingData" | "loadFailed" | "partOfIotSystem" | "networkScan";

interface Props {
    width?: number;
    /**
     * Front glyph shown over the device-to-cloud scene:
     * - "notAllowed": crossed-out bolt — the product is not configured for IoT.
     * - "missingData": question mark — required asset/customer data is missing.
     * - "loadFailed": warning triangle — the connection form could not be loaded.
     * - "partOfIotSystem": crossed-out bolt — the asset is part of an IoT system and cannot have its own connection.
     * - "networkScan": crossed-out signal arcs — the effective network scan is missing or not GREEN (IBM-1039).
     */
    variant: ConnectionUnavailableVariant;
}

/**
 * Illustration for IoT-connection unavailable states: a miniature of the
 * connection being created — a device card wired to a cloud — with a glyph
 * in front explaining why the wizard cannot be shown.
 *
 * Colors come from the application theme: the brand primary for the glyph,
 * neutral greys for the device, cloud and connector.
 */
const ConnectionUnavailableIllustration = ({width = 240, variant}: Props) => {
    const theme = useTheme();

    const backdrop = theme.palette.grey[100];
    const cardFill = theme.palette.common.white;
    const border = theme.palette.grey[300];
    const titleLine = theme.palette.grey[300];
    const subLine = theme.palette.grey[200];
    const primary = theme.palette.primary.main;
    const white = theme.palette.common.white;

    return (
        <svg
            width={width}
            viewBox="0 0 220 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="presentation"
            aria-hidden="true"
        >
            {/* soft backdrop */}
            <circle cx="110" cy="96" r="92" fill={backdrop} />

            {/* device card */}
            <rect x="26" y="52" width="62" height="48" rx="7" fill={cardFill} stroke={border} strokeWidth="1.5" />
            <rect x="33" y="60" width="20" height="20" rx="4" fill={border} />
            <rect x="59" y="62" width="22" height="5" rx="2.5" fill={titleLine} />
            <rect x="59" y="72" width="14" height="4" rx="2" fill={subLine} />
            <rect x="33" y="86" width="48" height="4" rx="2" fill={subLine} />

            {/* cloud */}
            <path
                d="M143 96 a11 11 0 0 1 -1 -22 a15 15 0 0 1 29 -4 a12 12 0 0 1 3 26 z"
                fill={cardFill}
                stroke={border}
                strokeWidth="1.5"
            />

            {/* dashed connector between device and cloud */}
            <path
                d="M92 76 H136"
                stroke={border}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="1 8"
            />

            {/* halo so the glyph reads as being in front of the scene */}
            <circle cx="110" cy="140" r="37" fill={white} />

            {(variant === "notAllowed" || variant === "partOfIotSystem") && (
                <>
                    {/* bolt with prohibition slash */}
                    <path
                        d="M114 116 L96 143 h11 l-5 21 L122 136 h-11 l5 -20 z"
                        fill={border}
                    />
                    <circle cx="110" cy="140" r="28" stroke={primary} strokeWidth="5" />
                    <path d="M90 120 L130 160" stroke={primary} strokeWidth="5" strokeLinecap="round" />
                </>
            )}

            {variant === "missingData" && (
                <>
                    {/* question mark */}
                    <circle cx="110" cy="140" r="26" fill={primary} />
                    <path
                        d="M101 133 a9.5 9.5 0 1 1 13 9 c-3 1.5 -4 3 -4 6"
                        stroke={white}
                        strokeWidth="5"
                        strokeLinecap="round"
                        fill="none"
                    />
                    <circle cx="110" cy="157" r="3.2" fill={white} />
                </>
            )}

            {variant === "networkScan" && (
                <>
                    {/* signal arcs with prohibition slash */}
                    <circle cx="110" cy="154" r="4" fill={border} />
                    <path d="M98 144 a17 17 0 0 1 24 0" stroke={border} strokeWidth="5" strokeLinecap="round" fill="none" />
                    <path d="M89 134 a30 30 0 0 1 42 0" stroke={border} strokeWidth="5" strokeLinecap="round" fill="none" />
                    <circle cx="110" cy="140" r="28" stroke={primary} strokeWidth="5" />
                    <path d="M90 120 L130 160" stroke={primary} strokeWidth="5" strokeLinecap="round" />
                </>
            )}

            {variant === "loadFailed" && (
                <>
                    {/* warning triangle */}
                    <path
                        d="M110 114 L136 161 a4 4 0 0 1 -3.5 6 h-45 a4 4 0 0 1 -3.5 -6 z"
                        fill={primary}
                    />
                    <path d="M110 132 v18" stroke={white} strokeWidth="5" strokeLinecap="round" />
                    <circle cx="110" cy="159" r="3.2" fill={white} />
                </>
            )}
        </svg>
    );
};

export default ConnectionUnavailableIllustration;
